import React from 'react';
import { ArrowUpRightIcon, MousePointerClickIcon, XIcon } from 'lucide-react';
import type { Activity, DetailField } from '../../types/activity';
import { ChannelBadge } from './ChannelBadge';
import { OutcomeTag } from './OutcomeTag';
import { SmsThread } from './SmsThread';
import { channelLabel, formatFullTimestamp } from '../../utils/timeline';
interface ActivityDetailProps {
  activity: Activity | null;
  onClose: () => void;
  showClose?: boolean;
}
function DetailList({
  fields
}: {
  fields: DetailField[];
}) {
  if (fields.length === 0) {
    return <p className="rounded-lg border border-dashed border-line px-3 py-3 text-[12.5px] text-ink-muted" data-id="element-2" data-mp="3bgerjqg0eylq">
        No delivery details were recorded for this entry.
      </p>;
  }
  return <dl className="divide-y divide-line" data-id="element-3" data-mp="32fqaemvbki4m">
      {fields.map(field => <div key={field.label} className="flex items-baseline justify-between gap-4 py-2" data-id="element-4" data-mp="2xhf3ajweslq3">
          <dt className="text-[12.5px] text-ink-muted" data-id="element-5" data-mp="2slq9kx72lz1p">{field.label}</dt>
          <dd className="text-right text-[12.5px] font-medium text-ink" data-id="element-6" data-mp="2h4v9319x0jgs">
            {field.value}
          </dd>
        </div>)}
    </dl>;
}
export function ActivityDetail({
  activity,
  onClose,
  showClose = false
}: ActivityDetailProps) {
  if (!activity) {
    return <div className="flex h-full flex-col items-center justify-center gap-2 px-8 text-center" data-id="element-7" data-mp="2d54ml9rf8pi2">
        <MousePointerClickIcon className="h-5 w-5 text-ink-subtle" aria-hidden="true" data-id="element-8" data-mp="30up6pvzb9ssx" />
        <p className="font-display text-[13.5px] font-bold text-ink" data-id="element-9" data-mp="34txw6onbj4io">
          Select an activity
        </p>
        <p className="max-w-[220px] text-[12.5px] leading-relaxed text-ink-muted" data-id="element-10" data-mp="2qv827hvspkyj">
          Transcripts, notes, and the full text conversation open here without
          leaving the timeline.
        </p>
      </div>;
  }
  const isSms = activity.channel === 'sms';
  return <article className="flex h-full min-h-0 flex-col" data-id="element-11" data-mp="1kg74p7r4gupu">
      <div className="flex items-start justify-between gap-3 border-b border-line px-5 py-4" data-id="element-12" data-mp="10d9s10s0w4s2">
        <div className="min-w-0" data-id="element-13" data-mp="wbo9ozwpyyah">
          <div className="flex items-center gap-2" data-id="element-14" data-mp="wcr0z8xyf2qx">
            <ChannelBadge channel={activity.channel} tone={activity.channel === 'call' && activity.outcome === 'connected' ? 'positive' : 'plain'} data-id="element-15" data-mp="we2559vbacik" />
            <span className="font-display text-[11px] font-bold uppercase tracking-[0.08em] text-ink-muted" data-id="element-16" data-mp="hnl4vtuexsn9">
              {channelLabel(activity.channel)}
            </span>
            <OutcomeTag outcome={activity.outcome} data-id="element-17" data-mp="8llo92nb8qku" />
          </div>
          <h3 className="mt-3 font-display text-[15px] font-bold leading-snug text-ink" data-id="element-18" data-mp="8nya3qw6vl36">
            {activity.title}
          </h3>
          <p className="mt-1 text-[12.5px] text-ink-subtle" data-id="element-19" data-mp="230ef6od90t6g">
            {formatFullTimestamp(activity.at)} · {activity.actor}
          </p>
        </div>
        {showClose && <button type="button" onClick={onClose} aria-label="Close details" className="rounded-md p-1.5 text-ink-subtle transition-colors duration-100 ease-emphasis hover:bg-surface-muted hover:text-ink" data-id="element-20" data-mp="25ku9ikqtkst8">
            <XIcon className="h-4 w-4" data-id="element-21" data-mp="2fcguh8sym696" />
          </button>}
      </div>

      {isSms ? <>
          <SmsThread focusActivityId={activity.id} data-id="element-22" data-mp="2kb9y89ig2iw5" />

          <div className="shrink-0 border-t border-line" data-id="element-23" data-mp="1mmqawk8k6c5o">
            <h4 className="px-5 pt-3 font-display text-[11px] font-bold uppercase tracking-[0.08em] text-ink-muted" data-id="element-24" data-mp="1rkrt72y5u7g3">
              Message details
            </h4>
            <div className="timeline-scroll max-h-[180px] overflow-y-auto px-5 pb-3 pt-1" data-id="element-25" data-mp="1wikzrx3dl3gw">
              <DetailList fields={activity.details} data-id="element-26" data-mp="20n159x92u8vl" />
            </div>
          </div>
        </> : <div className="timeline-scroll min-h-0 flex-1 space-y-6 overflow-y-auto px-5 py-5" data-id="element-27" data-mp="37yruoicnm55g">
          {activity.note && <section data-id="element-28" data-mp="3ajhgk6qx4k01">
              <h4 className="mb-2 font-display text-[11px] font-bold uppercase tracking-[0.08em] text-ink-muted" data-id="element-29" data-mp="3aixxgmpj7pkf">
                {activity.channel === 'event' ? 'What this means' : 'Notes'}
              </h4>
              <p className="rounded-md border-l-[3px] border-gold bg-surface-muted p-3 text-[13px] leading-relaxed text-ink" data-id="element-30" data-mp="3khx645aarl7f">
                {activity.note}
              </p>
            </section>}

          <section data-id="element-31" data-mp="3tha8yf3x1bj6">
            <h4 className="mb-2 font-display text-[11px] font-bold uppercase tracking-[0.08em] text-ink-muted" data-id="element-32" data-mp="3tgqwvf0y5hd2">
              Details
            </h4>
            <DetailList fields={activity.details} data-id="element-33" data-mp="73abpwwrogpo" />
          </section>
        </div>}

      <div className="flex items-center border-t border-line px-5 py-3" data-id="element-34" data-mp="23xrzl16zprb">
        <button type="button" className="inline-flex items-center gap-1.5 rounded-md border border-line-strong bg-surface px-3 py-1.5 font-display text-[12px] font-bold text-ink transition-colors duration-150 ease-emphasis hover:bg-gold-soft" data-id="element-35" data-mp="25qwatoc1xw5">
          View full record
          <ArrowUpRightIcon className="h-3.5 w-3.5 text-ink-subtle" data-id="element-36" data-mp="uob8ukqayry0" />
        </button>
      </div>
    </article>;
}